import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchArchdeaconries } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { MapPin, User, Church } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Archdeaconry {
  id: number;
  name: string;
  slug: string; // ✅ Add this
  archdeacon?: number;
  description?: string | null;
  parishes?: { id: number; name: string }[];
  current_archdeacon?: {
    id: number;
    title: string;
    name: string;
    photo?: string;
  };
  current_supervisor?: {
    id: number;
    title: string;
    name: string;
    photo?: string;
  };
}



const Archdeaconries = () => {
  const [archdeaconries, setArchdeaconries] = useState<Archdeaconry[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadArchdeaconries = async () => {
      try {
        const response = await fetchArchdeaconries();
        setArchdeaconries(response.data);
      } catch (error) {
        console.error('Error fetching archdeaconries:', error);

        // Fallback demo data
        const fallbackData: Archdeaconry[] = [
          {
            id: 1,
            name: 'Ibadan South Central',
            archdeacon: 1,
            slug: '',
            description: 'Covering the central areas of Ibadan South with focus on urban ministry.',
            parishes: [
              { id: 1, name: 'St. Peter\'s Church' },
              { id: 2, name: 'St. Luke\'s Church' },
              { id: 3, name: 'St. James Church' },
            ],
            current_archdeacon: {
              id: 1,
              title: 'Ven.',
              name: 'John Adebayo',
            },
          },
        ];

        setArchdeaconries(fallbackData);

        toast({
          title: 'Note',
          description: 'Using sample data. API connection will be established for live data.',
          variant: 'default',
        });
      } finally {
        setLoading(false);
      }
    };

    loadArchdeaconries();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading archdeaconries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our Archdeaconries
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Administrative divisions serving different areas of the Diocese of Ibadan South
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {archdeaconries.map((archdeaconry) => (
            <Card key={archdeaconry.id} className="border-none shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-t-lg">
                <CardTitle className="text-2xl lg:text-3xl">
                  {archdeaconry.name}
                </CardTitle>

                {archdeaconry.current_archdeacon ? (
                  <p className="text-purple-100 mt-2">
                    <User className="inline h-4 w-4 mr-2" />
                    Archdeacon: {archdeaconry.current_archdeacon.title} {archdeaconry.current_archdeacon.name}
                  </p>
                ) : archdeaconry.current_supervisor ? (
                  <p className="text-purple-100 mt-2">
                    <User className="inline h-4 w-4 mr-2" />
                    Supervisor: {archdeaconry.current_supervisor.title} {archdeaconry.current_supervisor.name}
                  </p>
                ) : (
                  <p className="text-purple-100 mt-2">
                    <User className="inline h-4 w-4 mr-2" />
                    No Archdeacon or Supervisor Assigned
                  </p>
                )}

                {archdeaconry.description && (
                  <p className="text-purple-50 mt-2 whitespace-pre-line">
                    {archdeaconry.description}
                  </p>
                )}
              </CardHeader>

              <CardContent className="p-6 bg-white">
                <p className="flex items-center text-gray-700 text-lg mb-2">
                  <Church className="h-5 w-5 mr-2 text-purple-600" />
                  Number of Parishes: {archdeaconry.parishes?.length ?? 0}
                </p>

                <Link
                  to={`/archdeaconries/${archdeaconry.slug}`}
                  className="inline-block mt-4 text-purple-700 font-semibold hover:underline"
                >
                  View Archdeaconry →
                </Link>
              </CardContent>

            </Card>
          ))}
        </div>

      </div>
    </div>
  );
};

export default Archdeaconries;
